/**
 * Portfolio — a full-width, image-led slideshow for new clients.
 *
 * Design intent: cinematic Scottish-highland feel, let the aerials do the
 * talking. Minimal text, manual controls, quiet transitions.
 *
 * SWAPPING IMAGES:
 *   The `SHOTS` array below is the ONLY place to edit. Each entry needs:
 *     { src, alt, title, location }
 *   Replace the placeholder Pexels URLs with your own hosted URLs (Cloudinary,
 *   S3, a subdomain — anything public). Order = display order. Add or remove
 *   entries freely; the slideshow controls adapt automatically.
 *
 * Interaction spec:
 *   • Auto-advance every 7 seconds (long enough to linger on a shot)
 *   • Play / Pause button toggles auto-advance
 *   • Prev / Next arrows (keyboard: ← → arrow keys)
 *   • Numbered dot indicators — click to jump
 *   • Space bar toggles play/pause
 *   • Left / right swipe on touch devices
 *   • Reduced-motion respected (auto-advance disables)
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Play, Pause, ArrowRight, Camera } from "lucide-react";
import usePageMeta from "@/lib/usePageMeta";
import { Reveal, MaskedLine, HighlandBackdrop } from "@/components/Reveal";

/* ------------------------------------------------------------------ */
/* ⇩ EDIT HERE — placeholder images. Swap for your own client work.   */
/* ------------------------------------------------------------------ */
const SHOTS = [
    {
        src: "https://images.pexels.com/photos/19593823/pexels-photo-19593823.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=2200",
        alt: "Misty aerial mountain valley with waterfalls",
        title: "Cloud & cascade",
        location: "Highlands",
    },
    {
        src: "https://images.pexels.com/photos/29353711/pexels-photo-29353711.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=2200",
        alt: "Highland ridge at golden hour",
        title: "The long ridge",
        location: "West Highlands",
    },
    {
        src: "https://images.pexels.com/photos/6280898/pexels-photo-6280898.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=2200",
        alt: "Sea cliff and stack from above",
        title: "Where the cliffs step out",
        location: "Isle of Skye",
    },
    {
        src: "https://images.pexels.com/photos/33677969/pexels-photo-33677969.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=2200",
        alt: "Glen road and distant loch",
        title: "The road into the glen",
        location: "Glencoe",
    },
    {
        src: "https://images.pexels.com/photos/16948691/pexels-photo-16948691.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=2200",
        alt: "Coastal town from above",
        title: "Coast at low tide",
        location: "Fife",
    },
    {
        src: "https://images.pexels.com/photos/34975303/pexels-photo-34975303.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=2200",
        alt: "Misty mountains with cascading waterfalls",
        title: "Sun through the mist",
        location: "Highlands",
    },
];
/* ------------------------------------------------------------------ */

const AUTO_MS = 7000;

export default function Portfolio() {
    usePageMeta({
        title: "Portfolio — DTScotslad",
        description: "A quiet, image-led portfolio of aerial films and stills from across Scotland. For commissions and collaborations.",
    });

    const [index, setIndex] = useState(0);
    const [playing, setPlaying] = useState(true);
    const [direction, setDirection] = useState(1); // 1 = next, -1 = prev
    const touchStartX = useRef(null);
    const touchStartY = useRef(null);
    const intervalRef = useRef(null);
    const prefersReducedMotion = useRef(false);

    const total = SHOTS.length;

    const go = useCallback((delta) => {
        setDirection(delta > 0 ? 1 : -1);
        setIndex((i) => (i + delta + total) % total);
    }, [total]);

    const jumpTo = useCallback((i) => {
        setDirection(i > index ? 1 : -1);
        setIndex(((i % total) + total) % total);
    }, [index, total]);

    // Auto-advance
    useEffect(() => {
        if (typeof window !== "undefined" && window.matchMedia) {
            prefersReducedMotion.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        }
        if (!playing || prefersReducedMotion.current) return;
        intervalRef.current = setInterval(() => go(1), AUTO_MS);
        return () => clearInterval(intervalRef.current);
    }, [playing, index, go]);

    // Keyboard controls
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "ArrowRight") { e.preventDefault(); go(1); }
            else if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
            else if (e.code === "Space") { e.preventDefault(); setPlaying((p) => !p); }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [go]);

    // Touch swipe
    const onTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
    };
    const onTouchEnd = (e) => {
        if (touchStartX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        const dy = e.changedTouches[0].clientY - touchStartY.current;
        // Only fire on a clear horizontal swipe (ignore diagonal scrolls).
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
            go(dx < 0 ? 1 : -1);
        }
        touchStartX.current = null;
        touchStartY.current = null;
    };

    const shot = SHOTS[index];

    return (
        <div className="relative" data-testid="portfolio-page">
            {/* ---- Minimal top hero ---- */}
            <section className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-8 pb-10 lg:pt-14 lg:pb-16">
                <Reveal>
                    <div className="overline mb-4" style={{ color: "#0055A4", fontWeight: 700 }}>
                        <span className="editorial-rule" style={{ background: "#0055A4" }} />
                        Portfolio
                    </div>
                </Reveal>
                <h1
                    className="font-serif font-light tracking-tighter leading-[0.9]"
                    style={{ fontSize: "clamp(2.6rem, 7vw, 6.5rem)" }}
                    data-testid="portfolio-title"
                >
                    <div><MaskedLine delay={0.1}>Aerials,</MaskedLine></div>
                    <div>
                        <MaskedLine delay={0.3}>
                            <span className="italic" style={{ color: "#0055A4" }}>quietly framed.</span>
                        </MaskedLine>
                    </div>
                </h1>
            </section>

            {/* ---- Full-width slideshow ---- */}
            <section
                className="relative w-full overflow-hidden bg-[var(--sb-bg)]"
                style={{ height: "min(88vh, 900px)" }}
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                data-testid="portfolio-slideshow"
                aria-roledescription="carousel"
                aria-label="Portfolio slideshow"
            >
                <AnimatePresence initial={false} custom={direction} mode="popLayout">
                    <motion.div
                        key={shot.src}
                        custom={direction}
                        initial={{ opacity: 0, x: direction * 60, scale: 1.03 }}
                        animate={{ opacity: 1, x: 0, scale: 1 }}
                        exit={{ opacity: 0, x: -direction * 60, scale: 1.03 }}
                        transition={{ duration: 1.05, ease: [0.16, 1, 0.3, 1] }}
                        className="absolute inset-0"
                        data-testid={`portfolio-slide-${index}`}
                    >
                        <img
                            src={shot.src}
                            alt={shot.alt}
                            className="w-full h-full object-cover"
                            draggable={false}
                        />
                        {/* Cinematic dark vignette at edges + bottom for legibility */}
                        <div
                            aria-hidden
                            className="absolute inset-0 pointer-events-none"
                            style={{ background: "radial-gradient(120% 90% at 50% 45%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.55) 100%)" }}
                        />
                        <div className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none bg-gradient-to-t from-[var(--sb-bg)]/90 via-[var(--sb-bg)]/30 to-transparent" />
                    </motion.div>
                </AnimatePresence>

                {/* ---- Caption (very light touch) ---- */}
                <motion.div
                    key={`caption-${index}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute left-6 lg:left-14 bottom-24 lg:bottom-28 max-w-md z-10"
                    data-testid="portfolio-caption"
                >
                    <div className="overline mb-2" style={{ color: "#a8d0ef", fontWeight: 600 }}>
                        {shot.location}
                    </div>
                    <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-white">
                        {shot.title}
                    </h2>
                </motion.div>

                {/* ---- Prev / Next arrows ---- */}
                <button
                    type="button"
                    onClick={() => go(-1)}
                    aria-label="Previous shot"
                    data-testid="portfolio-prev"
                    className="absolute left-3 lg:left-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 lg:w-14 lg:h-14 rounded-full flex items-center justify-center text-white transition-all hover:scale-110 active:scale-95"
                    style={{ background: "rgba(0,0,0,0.35)", backdropFilter: "blur(6px)" }}
                >
                    <ChevronLeft size={26} />
                </button>
                <button
                    type="button"
                    onClick={() => go(1)}
                    aria-label="Next shot"
                    data-testid="portfolio-next"
                    className="absolute right-3 lg:right-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 lg:w-14 lg:h-14 rounded-full flex items-center justify-center text-white transition-all hover:scale-110 active:scale-95"
                    style={{ background: "rgba(0,0,0,0.35)", backdropFilter: "blur(6px)" }}
                >
                    <ChevronRight size={26} />
                </button>

                {/* ---- Bottom controls: play/pause + dots + counter ---- */}
                <div className="absolute inset-x-0 bottom-6 lg:bottom-8 z-20 flex items-center justify-center gap-5 px-4">
                    <button
                        type="button"
                        onClick={() => setPlaying((p) => !p)}
                        aria-label={playing ? "Pause slideshow" : "Play slideshow"}
                        data-testid="portfolio-play-pause"
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white transition-all hover:scale-110 active:scale-95 shrink-0"
                        style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(6px)" }}
                    >
                        {playing ? <Pause size={16} fill="white" /> : <Play size={16} fill="white" />}
                    </button>
                    <div className="flex items-center gap-2 flex-wrap justify-center max-w-full" data-testid="portfolio-dots">
                        {SHOTS.map((_, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => jumpTo(i)}
                                aria-label={`Go to shot ${i + 1}`}
                                aria-current={i === index ? "true" : "false"}
                                data-testid={`portfolio-dot-${i}`}
                                className="transition-all"
                                style={{
                                    width: i === index ? "28px" : "10px",
                                    height: "3px",
                                    borderRadius: 2,
                                    background: i === index ? "#0055A4" : "rgba(255,255,255,0.45)",
                                }}
                            />
                        ))}
                    </div>
                    <div className="text-xs uppercase tracking-[0.28em] text-white/80 tabular-nums font-mono shrink-0" data-testid="portfolio-counter">
                        {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
                    </div>
                </div>

                {/* ---- Progress bar (auto-advance visual) ---- */}
                {playing && !prefersReducedMotion.current && (
                    <motion.div
                        key={`progress-${index}`}
                        className="absolute top-0 left-0 h-[2px] z-20"
                        style={{ background: "#0055A4" }}
                        initial={{ width: "0%" }}
                        animate={{ width: "100%" }}
                        transition={{ duration: AUTO_MS / 1000, ease: "linear" }}
                    />
                )}
            </section>

            {/* ---- Minimal commission CTA ---- */}
            <section className="max-w-4xl mx-auto px-6 lg:px-12 py-24 lg:py-36 text-center">
                <Reveal>
                    <div className="overline mb-4" style={{ color: "#0055A4", fontWeight: 700 }}>
                        <span className="editorial-rule" style={{ background: "#0055A4" }} />
                        Commissions
                    </div>
                </Reveal>
                <h2
                    className="font-serif tracking-tight leading-[0.95]"
                    style={{ fontSize: "clamp(2rem, 4.4vw, 3.6rem)" }}
                >
                    <MaskedLine delay={0.1}>
                        Have a place in mind?
                    </MaskedLine>
                </h2>
                <Reveal delay={0.35}>
                    <p className="text-[var(--sb-text-dim)] mt-6 max-w-xl mx-auto text-lg leading-relaxed">
                        I fly on quiet days, with a light kit, and share the raw stills within a week. If you&rsquo;d like a shoot, tell me where and when.
                    </p>
                </Reveal>
                <Reveal delay={0.5}>
                    <div className="flex flex-wrap items-center justify-center gap-4 mt-10">
                        <Link to="/book" className="btn-primary" data-testid="portfolio-book-cta">
                            <Camera size={18} /> Book a shoot <ArrowRight size={16} />
                        </Link>
                        <Link to="/contact" className="btn-outline" data-testid="portfolio-contact-cta">
                            Talk it through
                        </Link>
                    </div>
                </Reveal>
            </section>

            <HighlandBackdrop variant="soft" />
        </div>
    );
}
